import { useEffect, useState } from "react";
import { statusApi } from "../../api/status";
import type { OverviewData, PlatformSnapshot } from "../../types/status";

const PLATFORM_LABEL: Record<string, string> = { phoenix: "Phoenix", kraken: "Kraken" };

function cardState(snap: PlatformSnapshot) {
  const criticals = snap.active_incidents.filter((i) => i.severity === "critical").length;
  if (snap.overall === "outage" || criticals > 0) {
    return {
      dot: "bg-red-500",
      border: "border-l-red-500",
      note: `${criticals || 1} critical ongoing`,
      noteCls: "text-red-500",
    };
  }
  if (snap.overall === "degraded") {
    return {
      dot: "bg-amber-500",
      border: "border-l-amber-500",
      note: `${snap.active_incidents.length} ongoing`,
      noteCls: "text-amber-500",
    };
  }
  return { dot: "bg-green-500", border: "border-l-green-500", note: "Healthy", noteCls: "text-gray-400" };
}

export default function PlatformStatusCards({ days }: { days: number }) {
  const [data, setData] = useState<OverviewData | null>(null);

  useEffect(() => {
    let active = true;
    statusApi
      .getOverview({ days })
      .then((res) => {
        if (active) setData(res.data);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [days]);

  if (!data) return null;

  const platforms: PlatformSnapshot[] = [data.phoenix];

  return (
    <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {platforms.map((snap) => {
        const state = cardState(snap);
        const uptime = snap.kpis.uptime.value;
        return (
          <div
            key={snap.platform}
            className={`rounded-xl border border-l-4 border-gray-200 bg-white px-5 py-4 ${state.border}`}
          >
            <div className="flex items-start justify-between">
              <span className="text-sm font-semibold text-gray-800">
                {PLATFORM_LABEL[snap.platform] ?? snap.platform}
              </span>
              <span className={`mt-1 h-2.5 w-2.5 rounded-full ${state.dot}`} aria-hidden="true" />
            </div>
            <p className="mt-2 text-2xl font-bold tabular-nums text-gray-900">
              {uptime != null ? `${uptime.toFixed(1)}%` : "—"}
            </p>
            <p className={`mt-0.5 text-xs ${state.noteCls}`}>{state.note}</p>
          </div>
        );
      })}
    </div>
  );
}
